import type { CheckboxChangeEvent, CheckboxRef } from 'beesoft-components';
import { Checkbox as BeeSoftCheckbox } from 'beesoft-components';
import clsx from 'clsx';
import type { Ref } from 'react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import useTestTag from '../../../common/hooks/use-test-tag';
import RequiredIndicator from '../../common/required-indicator/required-indicator.component';
import type { DynamicFieldComponentProps } from '../../dynamic-form/dynamic-form.interfaces';
import ErrorLabel from '../../labels/error-label';

type CheckboxChangeEventValueType = string | number | unknown;
export interface CheckboxChangeEventType {
  name?: string;
  value: CheckboxChangeEventValueType;
  checked: boolean;
}

export interface CheckboxProps
  extends DynamicFieldComponentProps<CheckboxChangeEventValueType, CheckboxChangeEventType> {
  initialChecked?: boolean;
  partial?: boolean;
  showError?: boolean;
  useAnimation?: boolean;
}

const CheckboxComponent = (props: CheckboxProps, ref: Ref<CheckboxRef>) => {
  const {
    name,
    label,
    value,
    initialChecked = false,
    partial = false,
    useAnimation = false,
    onChange,
    readOnly,
    required,
    error,
    disabled,
    showError = true,
  } = props;

  const checkboxRef = useRef<CheckboxRef>(null);
  const [checkedState, setCheckedState] = useState(initialChecked);
  // ensure readOnly is always a boolean
  const isReadOnly = Boolean(readOnly) || Boolean(disabled);

  useEffect(() => {
    setCheckedState(initialChecked);
  }, [initialChecked]);

  const setChecked = useCallback((newCheckedState: boolean) => {
    // Update the internal checkbox state
    if (checkboxRef.current?.setChecked) {
      checkboxRef.current.setChecked(newCheckedState);
    }
  }, []);

  const setPartiallyChecked = useCallback((isPartial: boolean) => {
    // Update the internal checkbox partial state
    if (checkboxRef.current?.setPartiallyChecked) {
      checkboxRef.current.setPartiallyChecked(isPartial);
    }
  }, []);

  useImperativeHandle(ref, () => ({
    setChecked,
    setPartiallyChecked,
  }));

  const tagCreator = useTestTag(label || name || '');

  const handleOnChange = useCallback(
    (event?: CheckboxChangeEvent) => {
      if (event) {
        onChange?.({
          name: name || event.originalEvent?.target.name,
          value: value || event.value,
          checked: event.checked,
        });

        setCheckedState(event.checked);
      }
    },
    [name, value, onChange]
  );

  const isRequiredIndicatorVisible = required && !readOnly;

  const checkboxWrapperStyles = clsx('p-1', {
    'flex items-center gap-2': isRequiredIndicatorVisible,
  });

  return (
    <>
      <div className={checkboxWrapperStyles} ref={tagCreator(checkedState.toString())}>
        <BeeSoftCheckbox
          ref={checkboxRef}
          name={name}
          value={value}
          label={label}
          checked={checkedState}
          partial={partial}
          useAnimation={useAnimation}
          readOnly={isReadOnly}
          onChange={handleOnChange}
          className={'text-sm'}
        />
        <RequiredIndicator readOnly={isReadOnly} required={required} />
      </div>
      {error && showError && <ErrorLabel role="alert">{error}</ErrorLabel>}
    </>
  );
};

const Checkbox = forwardRef(CheckboxComponent);
export default Checkbox;
